/**
 * Staff Workforce Guide - Printable PDF Guide for Time Clock, Time Off, and Scheduling
 * Comprehensive guide for all staff members
 */
import { 
  Printer, Clock, Calendar, Smartphone, CheckCircle, 
  AlertTriangle, ChevronRight, LogIn, LogOut, Settings,
  ArrowLeftRight
} from 'lucide-react';

export default function StaffWorkforceGuidePage() {
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .page-break { page-break-after: always; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .guide-section { break-inside: avoid; }
        }
        @page {
          size: A4;
          margin: 15mm;
        }
      `}</style>

      {/* Print Header - Only visible on screen */}
      <div className="no-print bg-green-700 text-white py-4 px-6 sticky top-0 z-50 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">Bulaccino Cafe - Workforce Guide</h1>
              <p className="text-green-100 text-sm">Time Clock, Time Off &amp; Scheduling Instructions</p>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-6 py-2 bg-white text-green-700 rounded-lg font-medium hover:bg-green-50 transition-colors"
            data-testid="print-guide-btn"
          >
            <Printer className="w-5 h-5" />
            Print / Download PDF
          </button>
        </div>
      </div>

      {/* Printable Content */}
      <div className="max-w-4xl mx-auto p-6 bg-white text-gray-900">
        {/* Cover Page */}
        <div className="text-center py-12 border-b-4 border-green-600 mb-8">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <Clock className="w-10 h-10 text-green-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Staff Workforce Guide</h1>
          <p className="text-xl text-gray-600 mb-2">Bulaccino Cafe - Nadi, Fiji</p>
          <p className="text-gray-500">Time Clock | Time Off | Scheduling</p>
          <div className="mt-8 inline-block bg-amber-50 border border-amber-200 rounded-lg px-6 py-3">
            <p className="text-amber-800 font-medium">Pay Week: Monday - Sunday</p>
            <p className="text-amber-700 text-sm">Hours finalized by Tuesday | Payroll on Wednesday</p>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="mb-8 p-6 bg-gray-50 rounded-xl">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Table of Contents</h2>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-green-600" />
              <span>1. Getting Started - Device Registration</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-green-600" />
              <span>2. Time Clock - Clock In / Clock Out</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-green-600" />
              <span>3. Viewing Your Schedule</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-green-600" />
              <span>4. Requesting Time Off (PTO)</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-green-600" />
              <span>5. Shift Swaps</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-green-600" />
              <span>6. Payroll Information</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-green-600" />
              <span>7. Troubleshooting &amp; FAQ</span>
            </div>
          </div>
        </div>

        {/* Section 1: Device Registration */}
        <div className="guide-section mb-8 page-break">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">1. Getting Started - Device Registration</h2>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-blue-800 font-medium mb-2">Why Device Registration?</p>
            <p className="text-blue-700 text-sm">
              To prevent buddy punching, each staff member must register their personal phone before clocking in.
              You can only clock in/out from your registered device while connected to cafe Wi-Fi.
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Steps to Register Your Device:</h3>
            
            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">1</div>
              <div>
                <p className="font-medium text-gray-900">Connect to Cafe Wi-Fi</p>
                <p className="text-gray-600 text-sm">Connect your phone to the <strong>&quot;BC-Namaka-S&quot;</strong> Wi-Fi network</p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">2</div>
              <div>
                <p className="font-medium text-gray-900">Open Time Clock App</p>
                <p className="text-gray-600 text-sm">Go to <strong>bulaccinocafe.com/staff/clock</strong> on your phone browser</p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">3</div>
              <div>
                <p className="font-medium text-gray-900">Enter Your Staff PIN</p>
                <p className="text-gray-600 text-sm">Enter your 4 or 6 digit PIN that was assigned to you by your manager</p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">4</div>
              <div>
                <p className="font-medium text-gray-900">Register Device</p>
                <p className="text-gray-600 text-sm">Click &quot;Register This Device&quot; - your phone is now linked to your account</p>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Important Notes:</p>
                <ul className="text-amber-700 text-sm list-disc list-inside mt-1 space-y-1">
                  <li>You can only have 1 registered device at a time</li>
                  <li>If you get a new phone, ask a manager to deactivate your old device first</li>
                  <li>There is a 14-day cooldown before registering a new device</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Time Clock */}
        <div className="guide-section mb-8 page-break">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">2. Time Clock - Clock In / Clock Out</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Clock In */}
            <div className="border border-green-200 rounded-lg p-4 bg-green-50">
              <div className="flex items-center gap-2 mb-3">
                <LogIn className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold text-green-800">Clock In</h3>
              </div>
              <ol className="space-y-2 text-sm text-green-700">
                <li className="flex gap-2">
                  <span className="font-bold">1.</span>
                  <span>Connect to cafe Wi-Fi</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">2.</span>
                  <span>Open bulaccinocafe.com/staff/clock</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">3.</span>
                  <span>Enter your PIN to log in</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">4.</span>
                  <span>Tap the green &quot;CLOCK IN&quot; button</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">5.</span>
                  <span>Verify your time is recorded</span>
                </li>
              </ol>
            </div>

            {/* Clock Out */}
            <div className="border border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex items-center gap-2 mb-3">
                <LogOut className="w-5 h-5 text-red-600" />
                <h3 className="font-semibold text-red-800">Clock Out</h3>
              </div>
              <ol className="space-y-2 text-sm text-red-700">
                <li className="flex gap-2">
                  <span className="font-bold">1.</span>
                  <span>Connect to cafe Wi-Fi</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">2.</span>
                  <span>Open bulaccinocafe.com/staff/clock</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">3.</span>
                  <span>Enter your PIN to log in</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">4.</span>
                  <span>Tap the red &quot;CLOCK OUT&quot; button</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">5.</span>
                  <span>Review your hours worked</span>
                </li>
              </ol>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <h3 className="font-semibold text-gray-800">Clock-In Rules:</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Early Clock-In</p>
                  <p className="text-gray-600 text-sm">You can clock in up to 15 minutes early, but pay starts at your scheduled time</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Late Clock-In</p>
                  <p className="text-gray-600 text-sm">If you clock in more than 5 minutes late, it will be flagged for review</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Viewing Schedule */}
        <div className="guide-section mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">3. Viewing Your Schedule</h2>
          </div>

          <div className="space-y-4">
            <p className="text-gray-700">
              You can view your upcoming shifts in the Time Clock app:
            </p>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="font-semibold text-purple-800 mb-2">From Your Phone:</h3>
              <ol className="text-purple-700 text-sm space-y-1 list-decimal list-inside">
                <li>Go to bulaccinocafe.com/staff/clock</li>
                <li>Log in with your PIN</li>
                <li>Tap &quot;My Schedule&quot; tab</li>
                <li>View your shifts for the current and upcoming weeks</li>
              </ol>
            </div>

            <div className="bg-gray-100 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2">What You Can See:</h3>
              <ul className="text-gray-700 text-sm space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Your scheduled shifts (date, start time, end time)</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Break times</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Approved time off</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Total hours scheduled for the week</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 4: Time Off (PTO) */}
        <div className="guide-section mb-8 page-break">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">4. Requesting Time Off (PTO)</h2>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Types of Leave:</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-sm">PTO</span>
                </div>
                <div>
                  <p className="font-medium text-blue-800">Paid Time Off</p>
                  <p className="text-blue-600 text-xs">Vacation, personal days</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-red-600 font-bold text-sm">Sick</span>
                </div>
                <div>
                  <p className="font-medium text-red-800">Sick Leave</p>
                  <p className="text-red-600 text-xs">Illness, medical appointments</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 font-bold text-sm">Per</span>
                </div>
                <div>
                  <p className="font-medium text-purple-800">Personal Day</p>
                  <p className="text-purple-600 text-xs">Emergency, family matters</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <span className="text-gray-600 font-bold text-sm">Unp</span>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Unpaid Leave</p>
                  <p className="text-gray-600 text-xs">Extended leave (no pay)</p>
                </div>
              </div>
            </div>

            <h3 className="font-semibold text-gray-800 mt-6">How to Request Time Off:</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <ol className="text-blue-800 space-y-2">
                <li className="flex gap-2">
                  <span className="font-bold">1.</span>
                  <span>Speak with your manager first (for extended leave)</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">2.</span>
                  <span>Your manager will enter the request in the admin system</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">3.</span>
                  <span>The request will be reviewed and approved/denied</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">4.</span>
                  <span>Check your schedule to see if it was approved</span>
                </li>
              </ol>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-amber-800 text-sm">
                  <strong>Note:</strong> Please request time off at least 2 weeks in advance when possible.
                  Emergency requests will be considered on a case-by-case basis.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 5: Shift Swaps */}
        <div className="guide-section mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <ArrowLeftRight className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">5. Shift Swaps</h2>
          </div>

          <div className="space-y-4">
            <p className="text-gray-700">
              Need to swap a shift with a coworker? Here&apos;s how:
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <ol className="text-amber-800 space-y-2">
                <li className="flex gap-2">
                  <span className="font-bold">1.</span>
                  <span>Find a coworker willing to swap shifts with you</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">2.</span>
                  <span>Both of you must agree to the swap</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">3.</span>
                  <span>Inform your manager of the swap request</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">4.</span>
                  <span>Manager will submit the swap request in the system</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">5.</span>
                  <span>Once approved, check your updated schedule</span>
                </li>
              </ol>
            </div>

            <div className="bg-gray-100 rounded-lg p-4">
              <p className="text-gray-800 text-sm">
                <strong>Important:</strong> Shift swaps must be approved by management. 
                You are responsible for your original shift until the swap is officially approved.
              </p>
            </div>
          </div>
        </div>

        {/* Section 6: Payroll Information */}
        <div className="guide-section mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <span className="text-green-600 font-bold text-lg">$</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">6. Payroll Information</h2>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <Calendar className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <h3 className="font-bold text-green-800">Pay Week</h3>
                <p className="text-green-700 text-lg font-semibold">Monday - Sunday</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <Clock className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <h3 className="font-bold text-amber-800">Hours Finalized</h3>
                <p className="text-amber-700 text-lg font-semibold">By Tuesday</p>
                <p className="text-amber-600 text-xs">Managers verify all hours</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <CheckCircle className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <h3 className="font-bold text-blue-800">Payroll Day</h3>
                <p className="text-blue-700 text-lg font-semibold">Wednesday</p>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Important Payroll Notes:</p>
                <ul className="text-amber-700 text-sm list-disc list-inside mt-1 space-y-1">
                  <li>Always check your hours before Tuesday to report any discrepancies</li>
                  <li>Missing clock-ins/outs must be reported to your manager immediately</li>
                  <li>Overtime must be pre-approved by management</li>
                  <li>All time records are permanent after Wednesday&apos;s payroll</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Section 7: Troubleshooting */}
        <div className="guide-section mb-8 page-break">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">7. Troubleshooting &amp; FAQ</h2>
          </div>

          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Q: I can&apos;t clock in - &quot;Not connected to Wi-Fi&quot;</h3>
              <p className="text-gray-600 text-sm">
                <strong>A:</strong> Make sure you&apos;re connected to the <strong>&quot;Bulaccino Secure&quot;</strong> Wi-Fi network (not the customer &quot;Bulaccino-Free-WiFi&quot;). 
                Check that Wi-Fi is enabled on your phone and you have a strong signal. Try disconnecting and reconnecting.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Q: &quot;Device not registered&quot; error</h3>
              <p className="text-gray-600 text-sm">
                <strong>A:</strong> You need to register your device first. Follow the steps in Section 1. 
                If you recently got a new phone, ask your manager to deactivate your old device.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Q: I forgot to clock in/out</h3>
              <p className="text-gray-600 text-sm">
                <strong>A:</strong> Inform your manager immediately. They can add a manual time entry with the correct times. 
                Note: Manual entries are flagged and reviewed.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Q: My PIN doesn&apos;t work</h3>
              <p className="text-gray-600 text-sm">
                <strong>A:</strong> Make sure you&apos;re entering your PIN correctly (4 or 6 digits). 
                If you&apos;ve forgotten your PIN, ask your manager to reset it for you.
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Q: How do I see my total hours for the week?</h3>
              <p className="text-gray-600 text-sm">
                <strong>A:</strong> Log in to the Time Clock app and view your &quot;My Schedule&quot; or &quot;Time History&quot; section. 
                It shows your daily hours and weekly totals.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Reference Card */}
        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4 text-center">Quick Reference</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Time Clock URL</h3>
              <p className="text-gray-600 text-sm bg-white px-3 py-2 rounded font-mono">
                bulaccinocafe.com/staff/clock
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Wi-Fi Networks</h3>
              <p className="text-gray-600 text-sm">BC-Namaka-S</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Need Help?</h3>
              <p className="text-gray-600 text-sm">Contact your Manager or Supervisor</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Payroll Questions</h3>
              <p className="text-gray-600 text-sm">Speak to Owner/Accountant</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-gray-300 text-center text-xs text-gray-500">
          <p>Bulaccino Cafe - Nadi, Fiji | Staff Workforce Guide</p>
          <p className="mt-1">Document generated from bulaccinocafe.com</p>
          <p className="mt-1">Version 1.0 - June 2026</p>
        </div>
      </div>
    </>
  );
}
